import { memo, useRef, useState } from "react";
import {
	arrow,
	autoUpdate,
	flip,
	FloatingArrow,
	FloatingFocusManager,
	FloatingPortal,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import "./InfoPopover.scss";
import PropTypes from "prop-types";
import { InfoCircleFilledIcon } from "app/icons/navigation";

const InfoPopover = ({ children, portal = false }) => {
	const [isOpen, setIsOpen] = useState(false);
	const arrowRef = useRef(null);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [
			offset(8),
			flip({ fallbackAxisSideDirection: "end" }),
			shift({ padding: 16 }), // avoid popover to stick to mobile viewport edge
			arrow({
				element: arrowRef,
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

	const floatingFocusElement = (
		<FloatingFocusManager context={context} modal={false}>
			<div
				className="info-popover__content"
				ref={refs.setFloating}
				style={floatingStyles}
				{...getFloatingProps()}
			>
				<FloatingArrow
					ref={arrowRef}
					context={context}
					style={{ transform: "translateY(-1px)" }}
				/>

				{children}
			</div>
		</FloatingFocusManager>
	);

	const floatingElement = portal ? (
		<FloatingPortal>{floatingFocusElement}</FloatingPortal>
	) : (
		floatingFocusElement
	);

	return (
		<div className="info-popover">
			<button
				type="button"
				ref={refs.setReference}
				{...getReferenceProps()}
				className="info-popover__trigger"
			>
				<InfoCircleFilledIcon width={12} height={12} />
			</button>
			{isOpen && floatingElement}
		</div>
	);
};

InfoPopover.propTypes = {
	children: PropTypes.element,
	classNamePrefix: PropTypes.string,
	portal: PropTypes.bool,
};

export default memo(InfoPopover);
